import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertCircle } from 'lucide-react';
import { guardedReload, guardedNavigate } from '../../lib/apiGuard/guardedNavigation';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    // 将来的なエラー報告ポイント（Sentry等）
  }

  handleReload = (): void => {
    guardedReload();
  };

  handleGoHome = (): void => {
    guardedNavigate('/');
  };

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div
          data-testid="error-boundary-fallback"
          className="flex justify-center items-center min-h-screen bg-[#f5f5f5] p-6"
        >
          <div className="rounded-xl border border-border bg-white shadow-sm max-w-[480px] w-full text-center">
            <div className="p-6">
              <AlertCircle size={64} style={{ color: '#d32f2f', marginBottom: '16px' }} />
              <h2 className="text-xl font-semibold text-text-primary mb-2">
                エラーが発生しました
              </h2>
              <p className="text-sm text-text-secondary mb-4">
                予期しないエラーが発生しました。ページを再読み込みするか、ダッシュボードに戻ってください。
              </p>
              <div className="flex gap-2 justify-center">
                <button
                  className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium"
                  onClick={this.handleReload}
                >
                  ページを再読み込み
                </button>
                <button
                  className="border border-primary text-primary px-4 py-2 rounded-lg hover:bg-primary/10 transition-colors text-sm font-medium"
                  onClick={this.handleGoHome}
                >
                  ダッシュボードに戻る
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
